import { useMemo, useCallback } from 'react';

/**
 * Hook to compute block overlaps using a sweep-line algorithm.
 *
 * @param {Array} visibleBlocks - Array of block objects with id, fromWeek, toWeek, row
 * @param {Map} weekIndexMap - Map of week strings to their indices
 * @returns {Object} - { getOverlappingBlocks, isRightmostOverlappingBlock }
 */
const useBlockOverlaps = (visibleBlocks, weekIndexMap) => {
  const blockOverlaps = useMemo(() => {
    const overlapsMap = new Map();
    const rightmostMap = new Map();
    const rows = new Map();
    const blockMeta = new Map();

    visibleBlocks.forEach((block) => {
      const fromIndex = weekIndexMap.get(block.fromWeek);
      const toIndex = weekIndexMap.get(block.toWeek);

      if (fromIndex === undefined || toIndex === undefined) {
        overlapsMap.set(block.id, []);
        rightmostMap.set(block.id, false);
        return;
      }

      const entry = { block, fromIndex, toIndex };
      blockMeta.set(block.id, entry);
      overlapsMap.set(block.id, new Set());
      if (!rows.has(block.row)) {
        rows.set(block.row, []);
      }
      rows.get(block.row).push(entry);
    });

    // Sweep-line per row for overlaps
    rows.forEach((rowBlocks) => {
      rowBlocks.sort((a, b) => {
        if (a.fromIndex === b.fromIndex) return a.toIndex - b.toIndex;
        return a.fromIndex - b.fromIndex;
      });

      let active = [];
      rowBlocks.forEach((entry) => {
        active = active.filter((item) => item.toIndex >= entry.fromIndex);

        active.forEach((other) => {
          overlapsMap.get(entry.block.id).add(other.block);
          overlapsMap.get(other.block.id).add(entry.block);
        });

        active.push(entry);
      });
    });

    // Finalize overlap arrays and rightmost flags
    overlapsMap.forEach((overlappingSet, id) => {
      const entry = blockMeta.get(id);
      if (!entry) {
        overlapsMap.set(id, []);
        rightmostMap.set(id, false);
        return;
      }

      const overlappingBlocks = Array.from(overlappingSet);
      overlapsMap.set(id, overlappingBlocks);

      if (overlappingBlocks.length === 0) {
        rightmostMap.set(id, false);
        return;
      }

      const entryToIndex = entry.toIndex;
      const maxToIndex = Math.max(
        entryToIndex,
        ...overlappingBlocks.map((b) => weekIndexMap.get(b.toWeek) ?? -Infinity),
      );
      rightmostMap.set(id, entryToIndex === maxToIndex);
    });

    // Ensure every block has an entry
    visibleBlocks.forEach((block) => {
      if (!overlapsMap.has(block.id)) {
        overlapsMap.set(block.id, []);
      }
      if (!rightmostMap.has(block.id)) {
        rightmostMap.set(block.id, false);
      }
    });

    return { overlapsMap, rightmostMap };
  }, [visibleBlocks, weekIndexMap]);

  const getOverlappingBlocks = useCallback(
    (blockId) => blockOverlaps.overlapsMap.get(blockId) || [],
    [blockOverlaps],
  );

  const isRightmostOverlappingBlock = useCallback(
    (blockId) => blockOverlaps.rightmostMap.get(blockId) || false,
    [blockOverlaps],
  );

  return {
    getOverlappingBlocks,
    isRightmostOverlappingBlock,
  };
};

export default useBlockOverlaps;
